import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/common/PasswordInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, 
  Sparkle, 
  CircleNotch,
  User,
  Phone,
  Lock,
  UserGear
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const AuthPage = ({ onBack, onSuccess }) => {
  const { t, language, toggleLanguage } = useLanguage();
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({ phone: '', password: '' });
  const [registerData, setRegisterData] = useState({ phone: '', password: '', name: '', pharmacyCode: '' });
  const [isAgentRegistration, setIsAgentRegistration] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.phone, loginData.password);
      toast.success(language === 'fr' ? 'Connexion réussie!' : 'Login successful!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Identifiants invalides' : 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const pharmacyCode = isAgentRegistration ? registerData.pharmacyCode : null;
      await register(registerData.phone, registerData.password, registerData.name, pharmacyCode);
      const successMsg = isAgentRegistration 
        ? (language === 'fr' ? 'Compte conseiller créé!' : 'Agent account created!')
        : (language === 'fr' ? 'Compte créé!' : 'Account created!');
      toast.success(successMsg);
      onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (errorMsg === 'Code pharmacie invalide') {
        toast.error(language === 'fr' ? 'Code pharmacie invalide' : 'Invalid pharmacy code');
      } else {
        toast.error(errorMsg || (language === 'fr' ? 'Erreur d\'inscription' : 'Registration failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col font-body">
      {/* Header - Premium Green */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="rounded-xl hover:bg-gray-100"
                data-testid="back-to-public"
              >
                <ArrowLeft size={22} weight="bold" className="text-gray-600" />
              </Button>
            )}
            <div className="w-9 h-9 rounded-xl bg-[#2E7D32] flex items-center justify-center">
              <Sparkle size={20} weight="fill" className="text-white" />
            </div>
            <span className="font-display text-lg font-semibold text-gray-900">
              {language === 'fr' ? 'Connexion à votre profil' : 'Sign in to your profile'}
            </span>
          </div>
          
          <button
            onClick={toggleLanguage}
            className="px-4 py-2 rounded-xl bg-gray-100 text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors"
            data-testid="auth-language-toggle"
          >
            {language === 'fr' ? 'EN' : 'FR'}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#2E7D32] flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Sparkle size={32} weight="fill" className="text-white" />
            </div>
            <h1 className="font-display text-2xl font-bold text-gray-900 mb-2">
              {language === 'fr' ? 'Connexion à votre profil' : 'Sign in to your profile'}
            </h1>
            <p className="text-sm text-gray-500">
              {language === 'fr' ? 'Accès aux applications métier' : 'Access to business applications'}
            </p>
          </div>

          <Card className="border-gray-100 shadow-card rounded-2xl">
            <CardHeader className="pb-4">
              <CardTitle className="text-center text-gray-900 font-display">
                {activeTab === 'login' ? t('login') : t('register')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100 rounded-xl p-1">
                  <TabsTrigger value="login" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="login-tab">{t('login')}</TabsTrigger>
                  <TabsTrigger value="register" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="register-tab">{t('register')}</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-phone" className="text-gray-700">{language === 'fr' ? 'Numéro de téléphone' : 'Phone Number'}</Label>
                      <div className="relative">
                        <Phone size={18} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                          id="login-phone"
                          type="tel"
                          placeholder="77 123 45 67"
                          value={loginData.phone}
                          onChange={(e) => setLoginData({ ...loginData, phone: e.target.value })}
                          className="pl-10 py-5 bg-gray-50 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2E7D32]/20 focus:border-[#2E7D32]"
                          required
                          data-testid="login-phone-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-gray-700">{t('password')}</Label>
                      <div className="relative">
                        <Lock size={18} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <PasswordInput
                          id="login-password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          className="pl-10 py-5 bg-gray-50 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2E7D32]/20 focus:border-[#2E7D32]"
                          required
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] py-5 rounded-xl font-medium transition-colors"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? (
                        <CircleNotch size={20} weight="bold" className="animate-spin" />
                      ) : (
                        t('signIn')
                      )}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">{t('name')}</Label>
                      <Input
                        id="register-name"
                        type="text"
                        placeholder={language === 'fr' ? 'Prénom Nom' : 'First Last'}
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        className="input-default"
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-phone">{language === 'fr' ? 'Numéro de téléphone' : 'Phone Number'}</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
                        <Input
                          id="register-phone"
                          type="tel"
                          placeholder="77 123 45 67"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                          className="input-default pl-10"
                          required
                          data-testid="register-phone-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">{t('password')}</Label>
                      <PasswordInput
                        id="register-password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="input-default"
                        required
                        minLength={6}
                        data-testid="register-password-input"
                      />
                    </div>
                    
                    {/* Agent Registration Option */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="agent-registration"
                          checked={isAgentRegistration}
                          onCheckedChange={setIsAgentRegistration}
                          data-testid="agent-checkbox"
                          className="border-[#2E7D32] data-[state=checked]:bg-[#2E7D32]"
                        />
                        <Label 
                          htmlFor="agent-registration" 
                          className="text-sm font-normal cursor-pointer flex items-center gap-2 text-gray-700"
                        >
                          <UserGear size={18} weight="duotone" className="text-[#2E7D32]" />
                          {language === 'fr' ? 'Je suis conseiller en pharmacie' : 'I am a pharmacy advisor'}
                        </Label>
                      </div>
                      
                      {isAgentRegistration && (
                        <div className="mt-3 space-y-2 animate-fade-in-up">
                          <Label htmlFor="pharmacy-code">
                            {language === 'fr' ? 'Code pharmacie' : 'Pharmacy Code'}
                          </Label>
                          <Input
                            id="pharmacy-code"
                            type="text"
                            placeholder={language === 'fr' ? 'Entrez le code de votre pharmacie' : 'Enter your pharmacy code'}
                            value={registerData.pharmacyCode}
                            onChange={(e) => setRegisterData({ ...registerData, pharmacyCode: e.target.value })}
                            className="input-default"
                            required={isAgentRegistration}
                            data-testid="pharmacy-code-input"
                          />
                          <p className="text-xs text-[#78716C]">
                            {language === 'fr' 
                              ? 'Demandez ce code à votre responsable de pharmacie' 
                              : 'Ask your pharmacy manager for this code'}
                          </p>
                        </div>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] py-5 rounded-xl font-medium transition-colors"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <CircleNotch size={20} weight="bold" className="animate-spin" />
                      ) : (
                        t('signUp')
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
